import { AppMenuListItem } from '@m/alchemy-ui/AppMenuListItem';
import { AppMenuList } from '@m/alchemy-ui/AppMenuList';
import { AppMenuListExpandableItem } from '@m/alchemy-ui/AppMenuListExpandableItem';
import { matchPath } from 'react-router-dom';
import { toValidatedList } from 'common/utils/rbacHelpers';

import { trendsLinks } from 'common/constants/menuItems';
import { Permissions } from 'common/store/types';

import AppNavListItemTertiaryWithoutIcon from './AppNavListItemTertiaryWithoutIcon';
import MenuItem from './MenuItem';
import { trendsFilterDuplicator } from './TrendsFilterDuplicator';
import { Item } from './Nav.styled';
import { NavBarItem } from './types';

interface SubMenuItemsProps {
  subMenuLabel: string;
  linksArr: NavBarItem[];
}

const SubMenuItems = ({ subMenuLabel, linksArr }: SubMenuItemsProps) => {
  return (
    <div className="vertical-gray-dividers-inset">
      <p className="font-size-smallish padding-large margin-none alchemy-font font-weight-xlarge text-body">
        {subMenuLabel}
      </p>
      <ul className="padding-left-medium">
        {linksArr.map(({ url, key, label }) => {
          const formattedUrl = trendsFilterDuplicator.modifyUrl(
            window.location,
            url
          );

          const isItemActive = Boolean(
            matchPath(window.location.pathname, {
              path: url,
              exact: true,
            })
          );

          return (
            <AppMenuListItem key={key} isActive={isItemActive}>
              <Item href={formattedUrl}>{label}</Item>
            </AppMenuListItem>
          );
        })}
      </ul>
    </div>
  );
};

interface TrendsProps {
  permissions: Permissions;
  pathname: string;
  hasRBACEnabled: boolean;
  isMobileMenu?: boolean;
}

const Trends = ({
  permissions,
  pathname,
  hasRBACEnabled,
  isMobileMenu,
}: TrendsProps) => {
  const dashboardLinks: NavBarItem[] = [];
  const reportsLinks: NavBarItem[] = [];
  const { serviceRecovery, company, linkedAccounts, companyProgram } =
    trendsLinks.DashboardsSectionLinks;
  const { tags, reportingGroups } = trendsLinks.ReportsSectionLinks;

  if (hasRBACEnabled) {
    const validatedLinks = [
      ...toValidatedList(
        permissions.viewServiceRecoveryTrendsLink,
        serviceRecovery
      ),
      ...toValidatedList(permissions.viewCompanyTrends, company),
      ...toValidatedList(permissions.viewMultiOrganization, linkedAccounts),
      ...toValidatedList(permissions.viewCompanyProgram, companyProgram),
    ];

    dashboardLinks.push(...validatedLinks);

    permissions.viewCompanyTrends && reportsLinks.push(tags);
    permissions.viewReportingGroupsTrend && reportsLinks.push(reportingGroups);
  } else {
    if (permissions.viewTrendsDashboards) {
      // When Alchemy Navbar FF is enabled and view_trends_dashboards is true.
      // We show all the links in the dashboard section for certain users. app/view_models/layouts/header.rb#trends_link
      dashboardLinks.push(
        serviceRecovery,
        company,
        linkedAccounts,
        companyProgram
      );
    } else {
      const validatedLinks = [
        ...toValidatedList(
          permissions.viewServiceRecoveryTrendsLink,
          serviceRecovery
        ),
        ...toValidatedList(permissions.viewCompanyTrends, company),
        ...toValidatedList(permissions.viewMultiOrganization, linkedAccounts),
        ...toValidatedList(
          permissions.viewCompanyProgramDashboard,
          companyProgram
        ),
      ];
      dashboardLinks.push(...validatedLinks);
    }
    permissions.viewReportingGroupsTrend && reportsLinks.push(reportingGroups);
    permissions.viewCompanyTrends && reportsLinks.unshift(tags);
  }

  if (reportsLinks.length === 0 && dashboardLinks.length === 0) return null;

  const isTrendsActive = [...reportsLinks, ...dashboardLinks].some(link =>
    matchPath(pathname, {
      path: link.url,
      exact: true,
    })
  );

  const { agentTrends } = trendsLinks;
  // only teamMember item exists in the dashboardLinks array

  if (dashboardLinks.length === 1 && dashboardLinks[0].key === 'agent-trends') {
    const isLinkActive = Boolean(
      matchPath(pathname, {
        path: agentTrends.url,
      })
    );

    return (
      <MenuItem
        label={agentTrends.label}
        url={agentTrends.url}
        keyName={agentTrends.key}
        icon={null}
        isActive={isLinkActive}
        isMobileMenu={isMobileMenu}
      />
    );
  }

  return isMobileMenu ? (
    <AppMenuListExpandableItem label="Trends">
      {/* @ts-expect-error */}
      <AppMenuList className="padding-left-medium">
        <SubMenuItems subMenuLabel="Dashboards" linksArr={dashboardLinks} />
        {reportsLinks.length > 0 && (
          <SubMenuItems subMenuLabel="Reports" linksArr={reportsLinks} />
        )}
      </AppMenuList>
    </AppMenuListExpandableItem>
  ) : (
    <AppNavListItemTertiaryWithoutIcon
      priority="primary"
      isActive={isTrendsActive}
      label="Trends"
      overlay={() => (
        // AppMenuList accepts a className prop but it is not present in its interface.
        // @ts-expect-error
        <AppMenuList className="padding-none">
          <SubMenuItems subMenuLabel="Dashboards" linksArr={dashboardLinks} />
          {reportsLinks.length > 0 && (
            <SubMenuItems subMenuLabel="Reports" linksArr={reportsLinks} />
          )}
        </AppMenuList>
      )}
    />
  );
};

export default Trends;
